import {
  NotePencil,
  UsersThree,
  ChalkboardTeacher,
  GraduationCap,
  CalendarCheck,
  CalendarBlank,
  CheckCircle,
  ClockCounterClockwise,
  Star,
  Clock,
  ChatCircleDots,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { SectionShell } from "../../styleguide/Primitives";
import { Showcase } from "../../library/LibraryPrimitives";
import { AreaNode } from "../diagrams";
import { cn } from "@/lib/utils";

const STATES = [
  { icon: CalendarCheck, label: "Due Today", purpose: "Gently surfaced on Home", tone: "orange" },
  { icon: CalendarBlank, label: "Upcoming", purpose: "Plan ahead, no pressure", tone: "blue" },
  { icon: CheckCircle, label: "Completed", purpose: "Score, feedback & retry", tone: "teal" },
  { icon: ClockCounterClockwise, label: "Past Homework", purpose: "Part of the journey", tone: "neutral" },
];

const DETAIL = [
  { icon: Star, label: "Score", value: "9 / 10" },
  { icon: Clock, label: "Time spent", value: "6 min" },
  { icon: ChatCircleDots, label: "Feedback", value: "“Lovely effort!”" },
  { icon: ArrowClockwise, label: "Retry", value: "Always offered" },
];

export function HomeworkSection() {
  return (
    <SectionShell
      id="homework"
      eyebrow="Homework"
      eyebrowIcon={NotePencil}
      title="Tasks from grown-ups, made friendly"
      description="Homework set by parents, teachers or tutors flows into the same calm question experience. It's organised by when it's due, and completing it becomes a proud part of the learning story — never a chore."
    >
      <div className="space-y-8">
        <Showcase title="Where homework comes from" tone="muted">
          <div className="grid gap-4 sm:grid-cols-3">
            <AreaNode icon={UsersThree} label="Parents" purpose="Set practice at home" tone="orange" />
            <AreaNode icon={ChalkboardTeacher} label="Teachers" purpose="Assign class tasks" tone="blue" />
            <AreaNode icon={GraduationCap} label="Tutors" purpose="Target specific skills" tone="teal" />
          </div>
        </Showcase>

        <Showcase title="Homework states">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STATES.map((s) => (
              <AreaNode key={s.label} icon={s.icon} label={s.label} purpose={s.purpose} tone={s.tone} />
            ))}
          </div>
        </Showcase>

        <Showcase title="A completed task remembers everything" description="So children (and grown-ups) can see growth">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {DETAIL.map((d) => (
              <div key={d.label} className="rounded-2xl border border-border bg-card p-5 text-center shadow-soft">
                <span className={cn("mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-teal/15 text-teal-ink")}>
                  <d.icon size={24} weight="fill" />
                </span>
                <p className="mt-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{d.label}</p>
                <p className="font-display text-lg font-extrabold">{d.value}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm font-medium text-muted-foreground">Every completed task is saved to <strong className="text-foreground">Homework History</strong> and feeds <strong className="text-foreground">Progress</strong> — turning effort into a visible, growing journey.</p>
        </Showcase>
      </div>
    </SectionShell>
  );
}

export default HomeworkSection;
